import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './Tooltip.module.css';

// Keep the bubble at least this far from the viewport edges.
const VIEWPORT_PADDING = 8;

const InfoIcon = () => (
  <svg
    className={css.defaultIcon}
    width="14"
    height="14"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <circle cx="8" cy="4.7" r="0.95" fill="currentColor" />
    <rect x="7.2" y="6.8" width="1.6" height="5" rx="0.8" fill="currentColor" />
  </svg>
);

/**
 * A lightweight, accessible tooltip. The trigger is keyboard-focusable and the
 * bubble is revealed on hover and focus (works for touch via focus too).
 *
 * When no children are passed it renders a default info icon as the trigger,
 * which is handy for clarifying inline labels/values.
 *
 * The bubble is centered on the trigger but automatically nudged horizontally
 * so it never falls off the edge of the viewport; the arrow stays pointed at
 * the trigger.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.content the tooltip content (text or node). When empty, nothing is rendered.
 * @param {React.ReactNode} [props.children] the trigger element. Defaults to an info icon.
 * @param {'top'|'bottom'} [props.placement='top'] where the bubble appears relative to the trigger
 * @param {string} [props.ariaLabel] accessible label for the trigger; defaults to `content` when it is a string
 * @param {string} [props.className] extra classes for the trigger wrapper
 * @param {string} [props.rootClassName] overrides the trigger wrapper root class
 * @param {string} [props.bubbleClassName] extra classes for the tooltip bubble
 * @returns {JSX.Element|null}
 */
const Tooltip = props => {
  const {
    className,
    rootClassName,
    bubbleClassName,
    content,
    children,
    placement = 'top',
    ariaLabel,
  } = props;

  const bubbleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);

  const reposition = useCallback(() => {
    const bubble = bubbleRef.current;
    if (!bubble || typeof window === 'undefined') {
      return;
    }
    // Measure from the natural (centered) position, then compute how far the
    // bubble pokes out of the viewport on either side.
    bubble.style.setProperty('--tooltipShift', '0px');
    const rect = bubble.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;

    const overflowLeft = VIEWPORT_PADDING - rect.left;
    const overflowRight = rect.right - (viewportWidth - VIEWPORT_PADDING);

    let next = 0;
    if (overflowLeft > 0) {
      next = overflowLeft;
    } else if (overflowRight > 0) {
      next = -overflowRight;
    }
    setShift(next);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition, content]);

  if (content == null || content === '') {
    return children || null;
  }

  const classes = classNames(rootClassName || css.root, className);
  const bubbleClasses = classNames(css.bubble, css[placement], bubbleClassName, {
    [css.open]: open,
  });
  const label = ariaLabel || (typeof content === 'string' ? content : undefined);

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span
      className={classes}
      tabIndex={0}
      role="note"
      aria-label={label}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children || <InfoIcon />}
      <span
        ref={bubbleRef}
        className={bubbleClasses}
        role="tooltip"
        style={{ '--tooltipShift': `${shift}px`, '--tooltipArrow': `${-shift}px` }}
      >
        {content}
      </span>
    </span>
  );
};

export default Tooltip;
